/**
 * Auth Context
 * Управление состоянием авторизации и пользователя
 * 
 * @typedef {import('../api/auth/types').UserDto} UserDto
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { initAuthClient, setTokens as setClientTokens, clearTokens as clearClientTokens, hasCsrfToken } from '../api/authClient';
import * as usersApi from '../api/auth/usersApi';
import * as authApi from '../api/auth/authApi';
import { notify } from '../utils/notify';

/**
 * @typedef {Object} AuthContextValue
 * @property {UserDto|null} user - Данные текущего пользователя
 * @property {boolean} isAuthenticated - Авторизован ли пользователь
 * @property {boolean} isLoading - Идёт ли загрузка сессии
 * @property {Error|null} error - Ошибка при инициализации
 * @property {(email: string, password: string) => Promise<void>} login - Авторизовать пользователя
 * @property {() => Promise<void>} logout - Разавторизовать пользователя
 * @property {(data: Partial<UserDto>) => void} updateUser - Обновить текущего пользователя
 */

/** @type {React.Context<AuthContextValue|undefined>} */
const AuthContext = createContext(undefined);

/**
 * Провайдер для Auth Context
 * @param {{ children: React.ReactNode }} props
 */
export function AuthProvider({ children }) {
  /** @type {[UserDto|null, React.Dispatch<React.SetStateAction<UserDto|null>>]} */
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  /** @type {[Error|null, React.Dispatch<React.SetStateAction<Error|null>>]} */
  const [error, setError] = useState(null);

  // Инициализировать authClient один раз
  useEffect(() => {
    console.log('[AuthContext] Initializing authClient...');
    initAuthClient(import.meta.env.VITE_AUTH_API_URL ?? '');
    setIsInitialized(true);
    console.log('[AuthContext] authClient initialized');
  }, []);

  // Синхронизация logout между вкладками браузера
  useEffect(() => {
    const handleStorageChange = (event) => {
      // Когда CSRF token удаляется (logout в другой вкладке)
      if (event.key === 'auth_csrf_token' && event.newValue === null) {
        console.log('[AuthContext] Logout detected in another tab');
        clearClientTokens();
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  /**
   * Восстановить сессию при загрузке приложения
   */
  useEffect(() => {
    // Ждём пока authClient инициализируется
    if (!isInitialized) {
      console.log('[AuthContext] Waiting for initialization...');
      return;
    }

    console.log('[AuthContext] Starting session restore...');

    const restoreSession = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Проверить есть ли CSRF token (индикатор активной сессии)
        const hasSession = hasCsrfToken();
        console.log('[AuthContext] Restore session, has CSRF token:', hasSession);
        
        // Попробовать обновить access token (refresh token в httpOnly cookie)
        try {
          console.log('[AuthContext] Trying to refresh access token...');
          const tokens = await authApi.refreshToken();
          console.log('[AuthContext] Tokens refreshed:', !!tokens);
          setClientTokens(tokens);
        } catch (err) {
          console.log('[AuthContext] No valid session, user not authenticated');
          clearClientTokens();
          setIsAuthenticated(false);
          setUser(null);
          return;
        }

        // Получить данные текущего пользователя
        try {
          console.log('[AuthContext] Fetching user data...');
          const userData = await usersApi.getMe();
          console.log('[AuthContext] User data received:', userData);
          setUser(userData);
          setIsAuthenticated(true);
        } catch (err) {
          console.error('[AuthContext] Failed to fetch user data:', err);
          clearClientTokens();
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Session restore failed');
        console.error('[AuthContext] Session restore error:', error);
        setError(error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, [isInitialized]);

  /**
   * Авторизовать пользователя
   * @param {string} email
   * @param {string} password
   */
  const login = async (email, password) => {
    try {
      setError(null);
      setIsLoading(true);

      console.log('[AuthContext] Logging in...');
      
      // Авторизация
      const tokens = await authApi.login({ email, password });
      console.log('[AuthContext] Got tokens:', tokens);
      
      setClientTokens(tokens);

      // Получить данные пользователя
      console.log('[AuthContext] Fetching user data...');
      const userData = await usersApi.getMe();
      console.log('[AuthContext] Got user:', userData);
      
      setUser(userData);
      setIsAuthenticated(true);
      console.log('[AuthContext] Login successful!');
      notify.success(`Добро пожаловать, ${userData?.name || userData?.email || ''}`.trim());
    } catch (err) {
      console.error('[AuthContext] Login failed:', err);
      const error = err instanceof Error ? err : new Error('Login failed');
      setError(error);
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Разавторизовать пользователя
   */
  const logout = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Вызвать API logout для инвалидации refresh token на сервере
      try {
        await authApi.logout();
      } catch (err) {
        console.warn('[AuthContext] Logout API call failed:', err);
        // Продолжаем очистку локального состояния даже при ошибке
      }

      // Очистить токены и состояние
      clearClientTokens();
      setUser(null);
      setIsAuthenticated(false);
      notify.info('Сессия завершена');
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Logout failed');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Обновить данные пользователя в контексте
   * @param {Partial<UserDto>} data
   */
  const updateUser = (data) => {
    if (user) {
      setUser({ ...user, ...data });
    }
  };

  /** @type {AuthContextValue} */
  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook для использования Auth Context
 * @returns {AuthContextValue}
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook для проверки авторизации
 * @returns {boolean}
 */
export function useIsAuthenticated() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}

/**
 * Hook для получения текущего пользователя
 * @returns {UserDto|null}
 */
export function useUser() {
  const { user } = useAuth();
  return user;
}

/**
 * Hook для проверки роли пользователя
 * @returns {'user'|'admin'|null}
 */
export function useUserRole() {
  const { user } = useAuth();
  return user?.role ?? null;
}

/**
 * Hook для проверки является ли пользователь админом
 * @returns {boolean}
 */
export function useIsAdmin() {
  const role = useUserRole();
  return role === 'admin';
}
